import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Users, Coins, TrendingUp, Plus, ArrowUpRight, RefreshCw, Settings } from 'lucide-react';
import DistributorWeeklyCard from '../components/DistributorWeeklyCard';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DistributorDashboard = () => {
    const { user, refreshUser } = useAuth();
    const [stats, setStats] = useState({});
    const [operators, setOperators] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [transferOpen, setTransferOpen] = useState(false);
    const [selectedOp, setSelectedOp] = useState(null);
    const [transferAmount, setTransferAmount] = useState('');
    const [newOp, setNewOp] = useState({ email: '', password: '', name: '', credit_rate: 0.90 });
    const [actionLoading, setActionLoading] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const [statsRes, opsRes] = await Promise.all([
                axios.get(`${API}/distributor/stats`, { withCredentials: true }),
                axios.get(`${API}/distributor/operators`, { withCredentials: true })
            ]);
            setStats(statsRes.data);
            setOperators(opsRes.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleCreateOperator = async () => {
        if (!newOp.email || !newOp.password || !newOp.name) return;
        setActionLoading(true);
        try {
            await axios.post(`${API}/distributor/operators`, newOp, { withCredentials: true });
            setCreateOpen(false);
            setNewOp({ email: '', password: '', name: '', credit_rate: 0.90 });
            await fetchData();
        } catch (err) {
            alert(err.response?.data?.detail || 'Failed to create operator');
        } finally {
            setActionLoading(false);
        }
    };

    const handleTransfer = async () => {
        if (!selectedOp || !transferAmount || parseFloat(transferAmount) <= 0) return;
        setActionLoading(true);
        try {
            await axios.post(`${API}/distributor/operators/transfer`, {
                recipient_id: selectedOp.id, amount: parseFloat(transferAmount)
            }, { withCredentials: true });
            setTransferOpen(false);
            setTransferAmount('');
            setSelectedOp(null);
            await fetchData();
            refreshUser();
        } catch (err) {
            alert(err.response?.data?.detail || 'Transfer failed');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) return <div className="min-h-screen bg-[#050505]"><Header /><div className="flex justify-center py-24"><div className="spinner" /></div></div>;

    return (
        <div className="min-h-screen bg-[#050505]">
            <Header />
            <div className="max-w-6xl mx-auto px-4 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-black text-white">Distributor <span className="text-[#00FFFF]">Dashboard</span></h1>
                    <p className="text-[#A0AAB2] mt-1">Your network of operators &amp; weekly performance</p>
                </div>

                {/* This week — what we settle on */}
                <DistributorWeeklyCard />

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
                    <div className="card-dark p-5 text-center">
                        <Users className="w-7 h-7 text-[#00FFFF] mx-auto mb-2" />
                        <p className="text-2xl font-mono font-bold text-white">{stats.operator_count || 0}</p>
                        <p className="text-[#A0AAB2] text-xs">Operators</p>
                    </div>
                    <div className="card-dark p-5 text-center">
                        <TrendingUp className="w-7 h-7 text-[#39FF14] mx-auto mb-2" />
                        <p className="text-2xl font-mono font-bold text-[#39FF14]">{(stats.total_distributed || 0).toFixed(2)}</p>
                        <p className="text-[#A0AAB2] text-xs">Lifetime distributed</p>
                    </div>
                    <div className="card-dark p-5 text-center">
                        <Users className="w-7 h-7 text-[#FF007F] mx-auto mb-2" />
                        <p className="text-2xl font-mono font-bold text-white">{stats.total_players || 0}</p>
                        <p className="text-[#A0AAB2] text-xs">Total Players</p>
                    </div>
                </div>

                {/* Operators Table */}
                <div className="card-dark p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-white">Your Operators</h3>
                        <div className="flex gap-2">
                            <Button onClick={() => fetchData()} variant="outline" size="sm" className="border-white/10 text-[#A0AAB2]">
                                <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button onClick={() => setCreateOpen(true)} className="btn-primary" data-testid="create-operator-btn">
                                <Plus className="w-4 h-4 mr-2" />Create Operator
                            </Button>
                        </div>
                    </div>

                    {operators.length === 0 ? (
                        <p className="text-center text-[#A0AAB2] py-8">No operators yet. Create your first one!</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-white/10">
                                        <TableHead className="text-[#A0AAB2]">Name</TableHead>
                                        <TableHead className="text-[#A0AAB2]">Email</TableHead>
                                        <TableHead className="text-[#A0AAB2] text-right">Balance</TableHead>
                                        <TableHead className="text-[#A0AAB2] text-right">Rate</TableHead>
                                        <TableHead className="text-[#A0AAB2] text-right">Players</TableHead>
                                        <TableHead className="text-[#A0AAB2] text-right">Purchased</TableHead>
                                        <TableHead className="text-[#A0AAB2] text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {operators.map((op, i) => (
                                        <TableRow key={op.id} className="border-white/10" data-testid={`operator-row-${i}`}>
                                            <TableCell className="text-white font-medium">{op.name}</TableCell>
                                            <TableCell className="text-[#A0AAB2]">{op.email}</TableCell>
                                            <TableCell className="text-right font-mono text-[#FFD700]">{op.credits.toFixed(2)}</TableCell>
                                            <TableCell className="text-right font-mono text-white">${op.credit_rate.toFixed(2)}</TableCell>
                                            <TableCell className="text-right text-white">{op.player_count}</TableCell>
                                            <TableCell className="text-right font-mono text-[#A0AAB2]">{op.total_purchased.toFixed(2)}</TableCell>
                                            <TableCell className="text-right">
                                                <Button size="sm" onClick={() => { setSelectedOp(op); setTransferOpen(true); }}
                                                    className="bg-[#39FF14] text-black hover:bg-[#39FF14]/80" data-testid={`transfer-op-${i}`}>
                                                    <ArrowUpRight className="w-3 h-3 mr-1" />Load
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Operator Dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="bg-[#121212] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-white">Create Operator</DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">Create a new operator under your management</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="text-white">Name</Label>
                            <Input value={newOp.name} onChange={e => setNewOp({ ...newOp, name: e.target.value })}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="new-op-name" />
                        </div>
                        <div>
                            <Label className="text-white">Email</Label>
                            <Input type="email" value={newOp.email} onChange={e => setNewOp({ ...newOp, email: e.target.value })}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="new-op-email" />
                        </div>
                        <div>
                            <Label className="text-white">Password</Label>
                            <Input type="password" value={newOp.password} onChange={e => setNewOp({ ...newOp, password: e.target.value })}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="new-op-password" />
                        </div>
                        <div>
                            <Label className="text-white">Credit Rate (your sell price per credit)</Label>
                            <Input type="number" step="0.01" value={newOp.credit_rate} onChange={e => setNewOp({ ...newOp, credit_rate: parseFloat(e.target.value) || 0 })}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white" data-testid="new-op-rate" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={handleCreateOperator} disabled={actionLoading} className="btn-primary" data-testid="confirm-create-op">
                            {actionLoading ? 'Creating...' : 'Create Operator'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Transfer Dialog */}
            <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
                <DialogContent className="bg-[#121212] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-white">Load Credits to {selectedOp?.name}</DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Your balance: {(stats.credits || 0).toFixed(2)} credits
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <Input type="number" value={transferAmount} onChange={e => setTransferAmount(e.target.value)}
                            placeholder="Amount" className="bg-[#0a0a0a] border-white/10 text-white font-mono" data-testid="transfer-amount" />
                        <div className="flex gap-2">
                            {[100, 500, 1000, 2000].map(v => (
                                <button key={v} onClick={() => setTransferAmount(String(v))}
                                    className="px-3 py-1 text-xs rounded bg-white/10 text-[#A0AAB2] hover:bg-white/20">{v}</button>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={handleTransfer} disabled={actionLoading || !transferAmount} className="btn-primary" data-testid="confirm-transfer">
                            {actionLoading ? 'Transferring...' : 'Transfer Credits'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DistributorDashboard;
